import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
} from "@tanstack/react-query";
import { FieldValues, Path, UseFormSetError } from "react-hook-form";

import { formatErrorMessages } from "@/lib/utils";
import { toast } from "@/lib/myToast";
import { myAlert } from "@/lib/myAlert";
import { useId } from "react";

/**
 * Custom React Query mutation hook untuk form yang otomatis:
 * - Menampilkan loading dialog
 * - Menampilkan pesan sukses
 * - Menangani error validasi dari backend dan mengisi setError react-hook-form jika diberikan
 * - Bisa menyisipkan konfirmasi sebelum mutate (confirm modal)
 *
 * @template TData - Tipe data response dari mutation
 * @template TError - Tipe error dari mutation (biasanya any)
 * @template TVariables - Tipe payload yang dikirim
 * @template TForm - Tipe field form yang digunakan untuk setError
 */

type FormMutationOptions<
  TData,
  TError,
  TVariables,
  TForm extends FieldValues,
> = Omit<UseMutationOptions<TData, TError, TVariables>, "mutationFn"> & {
  mutationFn: (variables: TVariables) => Promise<TData>;
  messageMode?: "toast" | "dialog"; // Menambahkan pilihan mode "toast" atau "dialog"
  loadingMessage?: string;
  successMessage?: string;
  confirmMessage?: {
    title: string;
    description?: string;
  };
  setError?: UseFormSetError<TForm>; // Optional setError dari form
};

export function useFormMutation<
  TData,
  TError = any,
  TVariables = void,
  TForm extends FieldValues = FieldValues,
>(
  options: FormMutationOptions<TData, TError, TVariables, TForm>
): UseMutationResult<TData, TError, TVariables> {
  const {
    mutationFn,
    loadingMessage,
    successMessage,
    confirmMessage,
    setError,
    messageMode = "dialog",
    ...restOptions
  } = options;

  const uniqueId = useId();

  return useMutation<TData, TError, TVariables>({
    mutationFn: async (variables) => {
      // ✅ Kalau ada confirmMessage tapi user cancel, langsung return tanpa error
      if (confirmMessage) {
        const confirmed = await myAlert.confirm(
          confirmMessage.title,
          confirmMessage.description
        );
        if (!confirmed) {
          // tidak throw error, hanya hentikan eksekusi
          return Promise.reject(null);
        }
      }

      // ✅ Loading message
      if (messageMode === "toast") {
        toast.loading(loadingMessage || "Memproses...", undefined, {
          id: uniqueId,
        });
      } else {
        myAlert.loading(loadingMessage || "Memproses...");
      }

      const result = await mutationFn(variables);
      return result;
    },

    onSuccess: (data, variables, context) => {
      // ✅ Sukses message
      if (messageMode === "dialog") {
        myAlert.success(
          "Berhasil",
          successMessage ?? "Berhasil Melakukan Operasi"
        );
        setTimeout(() => myAlert.done(), 3000);
      } else {
        toast.success(
          "Berhasil",
          successMessage ?? "Berhasil Melakukan Operasi"
        );
      }

      restOptions.onSuccess?.(data, variables, context);
    },

    onError: (error: any, variables, context) => {
      // ✅ Kalau error null artinya user cancel → abaikan
      if (error === null) return;

      const rawData = error?.data || {};

      if (messageMode === "dialog") {
        myAlert.error(error?.message || "Error", formatErrorMessages(rawData));
      } else {
        toast.error("Error", error?.message || "Error");
      }

      // setError dari backend
      if (setError) {
        Object.entries(rawData).forEach(([field, err]) => {
          if (!err) return;
          const message = Array.isArray(err)
            ? err[0]
            : typeof err === "object" &&
                "_errors" in err &&
                Array.isArray(err._errors)
              ? err._errors[0]
              : "Invalid";

          setError(field as Path<TForm>, {
            type: "server",
            message,
          });
        });
      }

      restOptions.onError?.(error, variables, context);
    },

    onSettled: (...rest) => {
      toast.removeToast(uniqueId);
      restOptions.onSettled?.(...rest);
    },

    ...restOptions,
  });
}
